import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  People as UsersIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface NotificationHistoryProps {
  notifications: any[];
  onRefresh: () => void;
}

const PushNotificationHistory: React.FC<NotificationHistoryProps> = ({
  notifications,
  onRefresh,
}) => {
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'PARTIAL_SUCCESS':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'SCHEDULED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <SuccessIcon color="success" />;
      case 'PARTIAL_SUCCESS':
        return <ErrorIcon color="warning" />;
      case 'FAILED':
        return <ErrorIcon color="error" />;
      case 'SCHEDULED':
        return <PendingIcon color="info" />;
      default:
        return <PendingIcon />;
    }
  };

  const handleViewDetails = (notification: any) => {
    setSelectedNotification(notification);
    setViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setViewDialogOpen(false);
    setSelectedNotification(null);
  };

  const getDeliveryRate = (notification: any) => {
    if (notification.totalTargetUsers === 0) return 0;
    return (notification.successfulDeliveries / notification.totalTargetUsers) * 100;
  };

  if (!notifications || notifications.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No notifications sent yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Start sending notifications to see them appear here
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Notification History</Typography>
        <Button onClick={onRefresh} variant="outlined">
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Rules</TableCell>
              <TableCell>Target Users</TableCell>
              <TableCell>Success Rate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.map((notification) => (
              <TableRow key={notification.notificationId}>
                <TableCell>
                  <Typography variant="subtitle2" noWrap>
                    {notification.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {notification.message.substring(0, 50)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {notification.rules.map((rule: string) => (
                      <Chip
                        key={rule}
                        label={rule.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UsersIcon fontSize="small" color="action" />
                    {notification.totalTargetUsers}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={getDeliveryRate(notification)}
                      sx={{ width: 60, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption">
                      {Math.round(getDeliveryRate(notification))}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(notification.status)}
                    label={notification.status}
                    color={getStatusColor(notification.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimerIcon fontSize="small" color="action" />
                    {format(new Date(notification.sentAt), 'MMM dd, HH:mm')}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(notification)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Notification Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Notification Details
          <Typography variant="subtitle2" color="text.secondary">
            {selectedNotification?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Content</Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Title:</strong> {selectedNotification.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Message:</strong> {selectedNotification.message}
                      </Typography>
                      {selectedNotification.actionUrl && (
                        <Typography variant="body2">
                          <strong>Action URL:</strong> {selectedNotification.actionUrl}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Delivery Summary</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Total Target Users:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedNotification.totalTargetUsers}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Successful Deliveries:</Typography>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {selectedNotification.successfulDeliveries}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Failed Deliveries:</Typography>
                        <Typography variant="body2" color="error.main" fontWeight="bold">
                          {selectedNotification.failedDeliveries}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Success Rate:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {Math.round(getDeliveryRate(selectedNotification))}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Targeting Rules</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {selectedNotification.rules.map((rule: string) => (
                          <Chip
                            key={rule}
                            label={rule.replace(/_/g, ' ')}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      
                      <Typography variant="subtitle2" gutterBottom>Processing Time:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedNotification.processingTimeMs}ms
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {selectedNotification.deliveryStatuses && selectedNotification.deliveryStatuses.length > 0 && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Delivery Statuses</Typography>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>View Individual Delivery Statuses</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>User ID</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Platform</TableCell>
                                  <TableCell>Error Message</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedNotification.deliveryStatuses.map((status: any, index: number) => (
                                  <TableRow key={index}>
                                    <TableCell>{status.userId}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={status.status}
                                        color={status.status === 'SUCCESS' ? 'success' : 'error'}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>{status.platform || 'N/A'}</TableCell>
                                    <TableCell>
                                      {status.errorMessage || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </AccordionDetails>
                        </Accordion>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {selectedNotification.errors && selectedNotification.errors.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2" gutterBottom>Errors:</Typography>
                      {selectedNotification.errors.map((error: string, index: number) => (
                        <Typography key={index} variant="body2">
                          • {error}
                        </Typography>
                      ))}
                    </Alert>
                  </Grid>
                )}

                {selectedNotification.warnings && selectedNotification.warnings.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <Typography variant="subtitle2" gutterBottom>Warnings:</Typography>
                      {selectedNotification.warnings.map((warning: string, index: number) => (
                        <Typography key={index} variant="body2">
                          • {warning}
                        </Typography>
                      ))}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotificationHistory;
